import { nativeQueueDb, convertToQueueItem, type NativeQueueEntry } from './nativeQueueDb'
import type { Metadata, MetadataSummary, QueueItemT } from '@/lib/types/bgSyncTypes.ts'
import type { AxiosRequestConfig } from 'axios'
import {
  MAX_RETRIES,
  MAX_RETENTION_MS,
  MAX_QUEUE_SIZE,
  COMMENT_PREVIEW_LENGTH,
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
} from './syncConstants'
import { getApiBaseUrl } from '@/config/apiConfig'

// Dev-only debug logging
const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[NativeQueue]', ...args)

type QueueUpdateListener = () => void
type FailureListener = (meta: Metadata) => void
type SuccessListener = (meta: Metadata) => void

class NativeQueueManager {
  private listeners: Set<QueueUpdateListener> = new Set()
  private failureListeners: Set<FailureListener> = new Set()
  private successListeners: Set<SuccessListener> = new Set()
  private fastSyncRunning = false
  private mediaSyncRunning = false
  private syncPromise: Promise<void> | null = null

  addListener(listener: QueueUpdateListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  addFailureListener(listener: FailureListener): () => void {
    this.failureListeners.add(listener)
    return () => this.failureListeners.delete(listener)
  }

  addSuccessListener(listener: SuccessListener): () => void {
    this.successListeners.add(listener)
    return () => this.successListeners.delete(listener)
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener()
    })
  }

  private notifyFailure(meta: Metadata): void {
    this.failureListeners.forEach(listener => {
      listener(meta)
    })
  }

  private notifySuccess(meta: Metadata): void {
    this.successListeners.forEach(listener => {
      listener(meta)
    })
  }

  async enqueueRequest(
    config: AxiosRequestConfig,
    formData?: FormData
  ): Promise<{ status: string; metadata: Metadata }> {
    // Check queue size to prevent unbounded growth
    const currentSize = await nativeQueueDb.requests.count()
    if (currentSize >= MAX_QUEUE_SIZE) {
      throw new Error(
        `Queue is full (${MAX_QUEUE_SIZE} items). Please sync pending items or check your connection.`
      )
    }

    const url = config.url ?? ''
    const method = (config.method?.toUpperCase() ?? 'POST') as 'POST' | 'PATCH'
    const headers = config.headers as Record<string, string> ?? {}

    // Use API base URL for iOS native (capacitor://localhost won't work for fetch)
    const apiBaseUrl = getApiBaseUrl() || window.location.origin
    const path = new URL(url, apiBaseUrl).pathname
    const type = path.split('/').filter(Boolean).shift() ?? ''
    const isMedia = type === 'media_objects'

    let queueName: 'fastQueue' | 'mediaQueue'
    if (path.includes('/media_objects')) {
      queueName = 'mediaQueue'
    } else if (path.includes('/issues') || path.includes('/comments') || path.includes('/locations')) {
      queueName = 'fastQueue'
    } else {
      throw new Error(`Unknown endpoint for queuing: ${path}`)
    }

    let data: Record<string, unknown> = {}
    let fileBlob: Blob | null = null
    let fileName: string | null = null

    if (isMedia && formData) {
      // Handle FormData properly - preserve duplicate keys as arrays
      for (const [key, value] of formData.entries()) {
        if (key === 'file') continue // Handle file separately
        if (data[key] !== undefined) {
          // Convert to array if duplicate key
          if (!Array.isArray(data[key])) {
            data[key] = [data[key]]
          }
          (data[key] as unknown[]).push(value)
        } else {
          data[key] = value
        }
      }
      const file = formData.get('file')
      if (file instanceof Blob) {
        fileBlob = file
        fileName = file instanceof File ? file.name : 'file'
        debug(`File extracted from FormData: ${fileName}, size: ${file.size}, type: ${file.type}`)
      } else {
        debug(`WARNING: No file found in FormData, file value: ${typeof file}`)
      }
    } else if (config.data) {
      if (typeof config.data === 'string') {
        try {
          data = JSON.parse(config.data)
        } catch (error) {
          debug('Failed to parse request data:', error)
          throw new Error('Invalid request data format')
        }
      } else {
        data = config.data ?? {}
      }
    }

    const changedFields = Object.keys(data)

    const summary: MetadataSummary = {}
    if (changedFields.includes('labels')) summary.labels = data.labels as string[]
    if (changedFields.includes('status')) summary.status = data.status as string
    if (changedFields.includes('recipient')) summary.recipient = data.recipient as string
    if (changedFields.includes('location')) summary.location = data.location as string

    let name = isMedia ? fileName : (data.name as string | undefined)
    if (data.text) {
      const text = data.text as string
      name = text.substring(0, COMMENT_PREVIEW_LENGTH) + (text.length > COMMENT_PREVIEW_LENGTH ? '...' : '')
    }

    const metadata: Metadata = {
      method,
      type,
      changedFields,
      summary,
      // Native queue stores blobs separately, so we don't include file here
      // The name field contains the filename for media uploads
      name: name ?? '',
      mediaObjectUUID: isMedia ? (data.mediaObjectUUID as string | null) ?? null : null,
      requestUUID: (data.requestUUID as string) || crypto.randomUUID(),
      fileUUID: isMedia ? (data.fileUUID as string | null) ?? null : null,
      fileCacheKey: null,
      failed: false,
      retries: 0,
      permanentFailure: false,
      error: '',
    }

    // Wrap dedup check + blob storage + request insert in a single Dexie transaction
    // to prevent TOCTOU race where two identical requests both pass the dedup check
    // before either is inserted (e.g., double-tap on mobile).
    let result: { status: string; metadata: Metadata } | null = null
    let queued = false

    // Convert blob to ArrayBuffer outside the transaction (async blob reads
    // can cause the IndexedDB transaction to auto-commit on iOS WebKit)
    let preparedBlob: Blob | null = null
    let blobCacheKey: string | null = null
    if (fileBlob) {
      blobCacheKey = `/native-file-cache/${metadata.fileUUID ?? crypto.randomUUID()}`
      const arrayBuffer = await fileBlob.arrayBuffer()
      preparedBlob = new Blob([arrayBuffer], { type: fileBlob.type })
      debug(`Blob prepared: ${blobCacheKey}, original size: ${fileBlob.size}, converted size: ${preparedBlob.size}`)
    } else if (isMedia) {
      debug(`WARNING: Media upload queued without file blob`)
    }

    try {
      await nativeQueueDb.transaction(
        'rw',
        nativeQueueDb.requests,
        nativeQueueDb.blobs,
        async () => {
          const existingEntries = await nativeQueueDb.requests
            .filter(e => {
              const m = e.metadata
              const basicMatch =
                m.method === metadata.method &&
                m.type === metadata.type &&
                m.requestUUID === metadata.requestUUID

              // For media uploads, also require fileUUID match (same file)
              if (metadata.fileUUID && m.fileUUID) {
                return basicMatch && m.fileUUID === metadata.fileUUID
              }

              // For PATCH (edits), also require the same changedFields.
              // The issue's requestUUID links all edits to that issue, so
              // without this check, editing status then recipient would be
              // rejected as a duplicate (same method + type + requestUUID).
              // Matching changedFields catches true duplicates (double-click)
              // while allowing different field edits to queue independently.
              if (metadata.method === 'PATCH' && m.changedFields && metadata.changedFields) {
                const sameFields =
                  m.changedFields.length === metadata.changedFields.length &&
                  m.changedFields.every(f => metadata.changedFields!.includes(f))
                return basicMatch && sameFields
              }

              return basicMatch
            })
            .toArray()

          if (existingEntries.length > 0) {
            debug(`Duplicate entry ignored (${metadata.requestUUID})`)
            result = { status: 'duplicate', metadata }
            return // Abort rest of transaction body (Dexie still commits)
          }

          if (preparedBlob && blobCacheKey) {
            await nativeQueueDb.blobs.put({ id: blobCacheKey, blob: preparedBlob })

            // Verify blob was stored correctly
            const storedBlob = await nativeQueueDb.blobs.get(blobCacheKey)
            if (!storedBlob?.blob || storedBlob.blob.size === 0) {
              throw new Error(`Failed to store blob: verification failed, size: ${storedBlob?.blob?.size ?? 0}`)
            }
            debug(`Blob stored and verified: ${blobCacheKey}, size: ${storedBlob.blob.size}`)

            metadata.fileCacheKey = blobCacheKey
          }

          const bodyToStore = isMedia
            ? JSON.stringify({ ...data, file: undefined, fileCacheKey: metadata.fileCacheKey })
            : JSON.stringify(data)

          const entry: NativeQueueEntry = {
            queueName,
            url: new URL(url, apiBaseUrl).href,
            method,
            headers: { ...headers },
            body: bodyToStore,
            timestamp: Date.now(),
            metadata,
          }

          await nativeQueueDb.requests.add(entry)

          debug(`Request queued to ${queueName}: ${metadata.requestUUID}, fileUUID: ${metadata.fileUUID}, cacheKey: ${metadata.fileCacheKey}`)

          result = { status: 'queued', metadata }
          queued = true
        },
      )

      if (queued) {
        this.notifyListeners()
      }

      return result!
    } catch (error) {
      console.error('[NativeQueue] Failed to queue request:', error)
      throw new Error(
        `Failed to queue request: ${error instanceof Error ? error.message : 'IndexedDB error'}`
      )
    }
  }

  async getAllEntries(): Promise<QueueItemT[]> {
    const entries = await nativeQueueDb.requests.toArray()
    return entries.map(e => convertToQueueItem(e))
  }

  async deleteEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (entry?.metadata.fileCacheKey) {
      await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
    }
    await nativeQueueDb.requests.delete(id)
    debug(`Deleted entry ${id}`)
    this.notifyListeners()
  }

  async deleteAllFailed(): Promise<number[]> {
    const entries = await nativeQueueDb.requests.filter(e => Boolean(e.metadata.failed) || Boolean(e.metadata.permanentFailure)).toArray()

    const deletedIds: number[] = []
    for (const entry of entries) {
      if (entry.id !== undefined) {
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
        deletedIds.push(entry.id)
      }
    }

    debug(`Deleted ${deletedIds.length} failed entries`)
    this.notifyListeners()

    return deletedIds
  }

  async retryEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (!entry) return

    await nativeQueueDb.requests.update(id, {
      metadata: {
        ...entry.metadata,
        failed: false,
        permanentFailure: false,
        retries: 0,
        error: '',
      },
      timestamp: Date.now(),
    })

    this.notifyListeners()
    await this.performSync()
  }

  async performSync(): Promise<void> {
    // Mutex: if sync is already running, wait for it instead of starting another
    if (this.syncPromise) {
      debug('Sync already in progress, waiting...')
      return this.syncPromise
    }

    this.syncPromise = this.doPerformSync()
    try {
      await this.syncPromise
    } finally {
      this.syncPromise = null
    }
  }

  private async doPerformSync(): Promise<void> {
    debug('performSync started')

    // Clean up stale entries (older than 7 days) before syncing
    await this.cleanupStaleEntries()

    const fastEntries = await nativeQueueDb.requests.where('queueName').equals('fastQueue').toArray()

    const mediaEntries = await nativeQueueDb.requests.where('queueName').equals('mediaQueue').toArray()

    await Promise.all([
      this.processFastQueue(fastEntries.filter(e => !e.metadata.permanentFailure)),
      this.processMediaQueue(mediaEntries.filter(e => !e.metadata.permanentFailure)),
    ])
  }

  /**
   * Remove entries older than MAX_RETENTION_MS (7 days) to prevent unbounded growth
   */
  private async cleanupStaleEntries(): Promise<void> {
    const cutoff = Date.now() - MAX_RETENTION_MS

    const staleEntries = await nativeQueueDb.requests
      .filter(e => e.timestamp < cutoff)
      .toArray()

    if (staleEntries.length === 0) return

    debug(`Cleaning up ${staleEntries.length} stale entries`)

    for (const entry of staleEntries) {
      if (entry.id !== undefined) {
        // Clean up associated blob if exists
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
      }
    }

    this.notifyListeners()
  }

  private async processFastQueue(entries: NativeQueueEntry[]): Promise<void> {
    if (this.fastSyncRunning || entries.length === 0) return
    this.fastSyncRunning = true

    try {
      for (const entry of entries) {
        await this.processEntry(entry)
      }
    } finally {
      this.fastSyncRunning = false
    }
  }

  private async processMediaQueue(entries: NativeQueueEntry[]): Promise<void> {
    if (this.mediaSyncRunning || entries.length === 0) return
    this.mediaSyncRunning = true

    try {
      for (const entry of entries) {
        await this.processEntry(entry)
      }
    } finally {
      this.mediaSyncRunning = false
    }
  }

  private async processEntry(entry: NativeQueueEntry): Promise<void> {
    if (entry.id === undefined) return

    const meta: Metadata = { ...entry.metadata }

    // Skip if entry has a future retry time (exponential backoff)
    const nextRetryAt = meta.nextRetryAt as number | undefined
    if (nextRetryAt && Date.now() < nextRetryAt) {
      debug(`Skipping ${meta.requestUUID}, retry scheduled for ${new Date(nextRetryAt).toISOString()}`)
      return
    }

    try {
      let requestInit: RequestInit = {
        method: entry.method,
        headers: { ...entry.headers, 'X-Native-Queue-Replay': 'true' },
        credentials: 'include',
      }

      if (meta.type === 'media_objects' && meta.fileCacheKey) {
        const cachedBlob = await nativeQueueDb.blobs.get(meta.fileCacheKey)

        // CRITICAL: If blob cannot be retrieved, fail the entry - don't send without file
        if (!cachedBlob?.blob) {
          debug(`Blob not found for ${meta.requestUUID}, fileUUID: ${meta.fileUUID}, cacheKey: ${meta.fileCacheKey}`)
          meta.failed = true
          meta.permanentFailure = true
          meta.error = 'File data lost - please try uploading again'

          await nativeQueueDb.requests.update(entry.id, {
            metadata: meta,
            timestamp: Date.now(),
          })
          this.notifyFailure(meta)
          this.notifyListeners()
          return
        }

        debug(`Blob found for ${meta.requestUUID}, size: ${cachedBlob.blob.size}`)

        const formData = new FormData()
        formData.append('file', cachedBlob.blob, meta.name || 'file')

        let bodyData: Record<string, unknown> = {}
        if (entry.body) {
          try {
            bodyData = JSON.parse(entry.body)
          } catch {
            debug('Failed to parse entry body for', meta.requestUUID)
          }
        }
        Object.entries(bodyData).forEach(([key, value]) => {
          if (key !== 'file' && key !== 'fileCacheKey' && value !== undefined) {
            formData.append(key, String(value))
          }
        })

        const newHeaders: Record<string, string> = { ...entry.headers, 'X-Native-Queue-Replay': 'true' }
        delete newHeaders['content-type']
        delete newHeaders['Content-Type']

        requestInit = {
          method: entry.method,
          headers: newHeaders,
          body: formData,
          credentials: 'include',
        }
      } else if (entry.body) {
        requestInit.body = entry.body
      }

      const response = await fetch(entry.url, requestInit)

      if (!response.ok) {
        meta.retries = (meta.retries ?? 0) + 1
        meta.failed = true
        meta.error = `HTTP ${response.status}: ${response.statusText}`

        const isClientErr = response.status >= 400 && response.status < 500
        if (isClientErr || meta.retries >= MAX_RETRIES) {
          meta.permanentFailure = true
          this.notifyFailure(meta)
          // Keep blobs on permanent failure so user can retry after resetting.
          // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
        } else {
          // Calculate exponential backoff for next retry
          const backoffMs = Math.min(
            BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1),
            BACKOFF_MAX_MS
          )
          meta.nextRetryAt = Date.now() + backoffMs
          debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
        }

        await nativeQueueDb.requests.update(entry.id, {
          metadata: meta,
          timestamp: Date.now(),
        })
        this.notifyListeners()
        return
      }

      if (meta.fileCacheKey) {
        await nativeQueueDb.blobs.delete(meta.fileCacheKey)
      }
      await nativeQueueDb.requests.delete(entry.id)
      debug(`Successfully synced ${meta.requestUUID}`)
      this.notifySuccess(meta)
      this.notifyListeners()
    } catch (err) {
      meta.failed = true
      meta.retries = (meta.retries ?? 0) + 1
      meta.error = err instanceof Error ? err.message : 'Unknown error'

      if (meta.retries >= MAX_RETRIES) {
        meta.permanentFailure = true
        this.notifyFailure(meta)
        // Keep blobs on permanent failure so user can retry after resetting.
        // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
      } else {
        // Calculate exponential backoff for next retry
        const backoffMs = Math.min(
          BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1),
          BACKOFF_MAX_MS
        )
        meta.nextRetryAt = Date.now() + backoffMs
        debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
      }

      await nativeQueueDb.requests.update(entry.id, {
        metadata: meta,
        timestamp: Date.now(),
      })
      this.notifyListeners()
    }
  }
}

export const nativeQueueManager = new NativeQueueManager()
